import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL, registerPartnersBaseURL } from '#constants/hosts';
import { client } from '#queries/utils';
import { common as partnerRegisterCommon } from '#proto/index.qonto.registration.partner_register';
import { firstparty as registerPartnerRegistration } from '#proto/index.qonto.registration.register.partner_registrations';
import type {
  RegistrationJsonApiResponse,
  RegisterPartnersRegistrationResult,
  PartnerRegistrationJsonApiResponse,
} from './types';

const ApiRegistration = partnerRegisterCommon.v1.ApiRegistration;
const ApiOrganization = partnerRegisterCommon.v1.ApiOrganization;
const ApiStakeholder = partnerRegisterCommon.v1.ApiStakeholder;
const ApiPartnerRegistration = registerPartnerRegistration.v1.ApiPartnerRegistration;

export const registerPartnersQueries = {
  all: ['register-partners'] as const,
  registration: (id?: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'registration', id] as const,
      queryFn: async (): Promise<RegisterPartnersRegistrationResult> => {
        if (!id) {
          throw new Error('Registration ID is required');
        }

        const res = await client<RegistrationJsonApiResponse>(
          `${registerPartnersBaseURL}/client_api/registrations/${id}?include=organization,stakeholders`
        );

        const registration = ApiRegistration.fromJSON({
          ...res.data.attributes,
          id: res.data.id,
          organization_id: res.data.relationships.organization.data.id,
          stakeholder_ids: res.data.relationships.stakeholders.data.map(s => s.id),
        });

        const includedItems = res.included ?? [];

        const registrationStakeholders = includedItems
          .filter(item => item.type === 'stakeholders')
          .map(item =>
            ApiStakeholder.fromJSON({
              ...item.attributes,
              id: item.id,
              organization_id: res.data.relationships.organization.data.id,
              registration_id: res.data.id,
            })
          );

        const organizationId = res.data.relationships.organization.data.id;
        const organizationData = includedItems.find(
          item => item.type === 'organizations' && item.id === organizationId
        );

        if (!organizationData) {
          throw new Error(`Organization ${organizationId} not found in included resources`);
        }

        // Note: organization from ?include=organization does not populate `features`.
        // The Ember hook fetched features via a separate store.findAll('organization') call.
        // If features are needed, make a separate request to GET /client_api/organizations.
        const organization = ApiOrganization.fromJSON({
          ...organizationData.attributes,
          id: organizationData.id,
        });

        return {
          registration,
          stakeholders: registrationStakeholders,
          organization,
        };
      },
    }),
  partnerRegistration: (id: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'partner-registration', id] as const,
      queryFn: async () => {
        const json = await client<PartnerRegistrationJsonApiResponse>(
          `${registerBaseURL}/v1/partner_registrations/${id}`
        );

        return ApiPartnerRegistration.fromJSON({
          ...json.data.attributes,
          id: json.data.id,
          organization_id: json.data.relationships.organization.data.id,
        });
      },
    }),
};
